import { pageScrollBehavior } from "@/lib/scroll";

import { ArrowRight, Eye, ArrowUpRight, Check } from "lucide-react";
import { ReportSampleCard } from "@/components/ReportSampleCard";
import { HeroAddressSearch } from "@/components/HeroAddressSearch";
import { PDFPreviewModal } from "@/components/PDFPreviewModal";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSecureAuth } from "@/hooks/useSecureAuth";
import { trackCtaClick } from "@/lib/funnel";
import { useLocale } from "@/i18n/context";
import { dashboardPath } from "@/i18n/routes";
import { clearPendingOrder, getPendingOrder, orderFormSearch, type PendingOrder } from "@/lib/pendingOrder";
import { clearUnlockTarget } from "@/lib/unlockTarget";
import { useCreditBalance } from "@/features/dashboard/hooks/useCreditBalance";

export const Hero = () => {
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [restoredOrder, setRestoredOrder] = useState<PendingOrder | null>(null);
  useEffect(() => { setRestoredOrder(getPendingOrder()); }, []);
  const { user } = useSecureAuth();
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  // Same query (and cache entry) the Header and dashboard use, so the hero
  // never disagrees with the balance shown elsewhere.
  const { data: cachedBalance, refetch: refetchBalance } = useCreditBalance();

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: pageScrollBehavior() });
  };

  const handleAddressSelected = async (address: string, shouldScroll: boolean = true, propertyType?: string, apartmentNumber?: string) => {
    if (typeof window === 'undefined') return;
    
    // A newly chosen property replaces an abandoned preview-unlock intent.
    clearUnlockTarget();
    sessionStorage.setItem('selectedAddress', address);
    if (propertyType) {
      sessionStorage.setItem('selectedPropertyType', propertyType);
    }
    if (apartmentNumber) {
      sessionStorage.setItem('selectedApartmentNumber', apartmentNumber);
    } else {
      sessionStorage.removeItem('selectedApartmentNumber');
    }
    window.dispatchEvent(new Event('addressSelected'));
    
    if (user) {
      try {
        // The Header usually has this cached already; fetch only if it hasn't.
        const creditBalance = cachedBalance ?? (await refetchBalance()).data;

        if (creditBalance && creditBalance > 0) {
          // The dashboard form prefills from the URL, in the visitor's own
          // language. The stored copy is dropped: it exists to survive a
          // checkout, and left behind it would be auto-ordered again after
          // any later credit purchase in this tab.
          clearPendingOrder();
          navigate(`${dashboardPath(locale)}${orderFormSearch({ address, propertyType, apartmentNumber })}`);
          return;
        }
      } catch (error) {
        console.error('Error checking credit balance:', error);
      }
    }
    
    if (shouldScroll) {
      // Allow Pricing to render the confirmation before moving keyboard focus.
      requestAnimationFrame(() => {
        const next = document.getElementById("property-next-step");
        if (next) {
          const headerHeight = document.querySelector("header")?.getBoundingClientRect().height ?? 64;
          const announcementHeight = document.getElementById("announcement-bar")?.getBoundingClientRect().height ?? 0;
          next.style.scrollMarginTop = `${headerHeight + announcementHeight + 16}px`;
          next.focus({ preventScroll: true });
        }
        (next ?? document.getElementById("pricing"))?.scrollIntoView({ behavior: pageScrollBehavior(), block: "start" });
      });
    }
  };

  return (
    <section id="hero" className="marketing-hero">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="hero-layout">
          <div className="hero-copy">
            <p className="editorial-eyebrow"><span className="status-dot" />{t.hero.eyebrow}</p>
            <h1 className="hero-title">
              <span>{t.hero.titleLead}</span>{" "}
              <span className="hero-title-secondary">{t.hero.titleHighlight}</span>
            </h1>
            <p className="hero-description">{t.hero.subtitle}</p>
            <div className="hero-address-form">
              <p className="mb-3 text-sm font-semibold text-foreground">{t.brand.startLabel}</p>
              <HeroAddressSearch
                key={restoredOrder ? JSON.stringify(restoredOrder) : "new-property"}
                initialAddress={restoredOrder?.address}
                initialPropertyType={restoredOrder?.propertyType === "stanovanje" ? "stanovanje" : "hisa"}
                initialApartmentNumber={restoredOrder?.apartmentNumber}
                onAddressSelected={handleAddressSelected}
                submitLabel={t.brand.searchCta}
              />
            </div>
            <div className="hero-secondary-actions">
              <span className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Check className="h-3.5 w-3.5 text-primary" />{t.brand.freeNote}</span>
              <button type="button" className="text-link" onClick={() => {
                trackCtaClick("hero_preview");
                setShowPDFPreview(true);
              }}>
                <Eye className="h-4 w-4" />{t.hero.ctaPreview}<ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="hero-report-stage">
            <div className="report-stage-label"><span className="editorial-eyebrow">{t.brand.sampleLabel}</span><span className="text-xs text-muted-foreground">PDF</span></div>
            <ReportSampleCard />
            <button type="button" className="sample-stage-link" onClick={() => { trackCtaClick("hero_preview"); setShowPDFPreview(true); }}>
              <span>{t.brand.sampleCta}</span><ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="hero-source-line">
          <span>{t.hero.tagline}</span>
          <div className="flex flex-wrap items-center gap-x-7 gap-y-2" aria-label={t.brand.sources}>
            <span>GURS</span><span>ETN</span><span>e-Sodstvo</span>
          </div>
        </div>
      </div>

      <PDFPreviewModal
        isOpen={showPDFPreview}
        onClose={() => setShowPDFPreview(false)}
        onOrderReport={() => {
          trackCtaClick("pdf_preview_order");
          setShowPDFPreview(false);
          scrollToPricing();
        }}
      />
    </section>
  );
};
